import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Lock, Loader2, CheckCircle, LogOut } from 'lucide-react';
import { authService } from '../services/authService';
import { useAuthStore } from '../stores/authStore';

const changePasswordSchema = z.object({
  newPassword: z
    .string()
    .min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  confirmPassword: z
    .string()
    .min(8, 'La confirmation doit contenir au moins 8 caractères'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
});

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

interface ChangePasswordModalProps {
  isOpen: boolean;
  onSuccess: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onSuccess,
}) => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const tokens = useAuthStore((state) => state.tokens);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    setError(null);
    setIsLoading(true);

    try {
      if (!tokens?.accessToken) {
        throw new Error('Session invalide');
      }

      await authService.changePassword(
        {
          newPassword: data.newPassword,
          confirmPassword: data.confirmPassword,
        },
        tokens.accessToken
      );

      setSuccess(true);
      reset();

      // Wait a moment then redirect
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du changement de mot de passe');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
          {/* Icon and Title */}
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-primary-100 rounded-full">
              <Lock size={24} className="text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Changement de mot de passe requis
              </h3>
              <p className="text-sm text-gray-500">
                Bienvenue, {user?.firstName} !
              </p>
            </div>
          </div>

          {/* Info */}
          <p className="text-sm text-gray-600 mb-6">
            Pour des raisons de sécurité, vous devez créer un nouveau mot de passe pour accéder à votre compte.
          </p>

          {/* Success Message */}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <CheckCircle className="text-green-600" size={20} />
              <span className="text-green-800">Mot de passe modifié avec succès !</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* New Password Field */}
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Nouveau mot de passe
              </label>
              <div className="relative">
                <input
                  {...register('newPassword')}
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  autoComplete="new-password"
                  disabled={success}
                  className={`
                    block w-full px-4 py-3 pr-12 rounded-lg border shadow-sm
                    focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                    disabled:bg-gray-100 disabled:cursor-not-allowed
                    ${errors.newPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                  `}
                  placeholder="Minimum 8 caractères"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={success}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <input
                  {...register('confirmPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  autoComplete="new-password"
                  disabled={success}
                  className={`
                    block w-full px-4 py-3 pr-12 rounded-lg border shadow-sm
                    focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                    disabled:bg-gray-100 disabled:cursor-not-allowed
                    ${errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                  `}
                  placeholder="Confirmer le mot de passe"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={success}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || success}
              className="
                w-full flex items-center justify-center gap-2 px-4 py-3
                bg-primary-600 text-white font-medium rounded-lg
                hover:bg-primary-700 focus:outline-none focus:ring-2
                focus:ring-offset-2 focus:ring-primary-500
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Changement en cours...
                </>
              ) : success ? (
                <>
                  <CheckCircle className="h-5 w-5" />
                  Mot de passe modifié
                </>
              ) : (
                <>
                  <Lock className="h-5 w-5" />
                  Changer le mot de passe
                </>
              )}
            </button>

            {/* Escape hatch: this modal is mandatory for THIS account, but the
                person may simply want to sign in as someone else. */}
            <button
              type="button"
              onClick={() => {
                logout();
                window.location.href = '/login';
              }}
              disabled={isLoading}
              className="
                w-full mt-3 flex items-center justify-center gap-2 px-4 py-2.5
                border border-gray-300 text-gray-700 font-medium rounded-lg
                hover:bg-gray-50 focus:outline-none focus:ring-2
                focus:ring-offset-2 focus:ring-gray-300
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              <LogOut className="h-4 w-4" />
              Utiliser un autre compte
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
